import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { AdminHeader, AdminSidebar } from "../components/AdminHeader";
import {
  Settings,
  Bell,
  Clock,
  Mail,
  Play,
  Save,
  Building2,
  Upload,
  Trash2,
  Phone,
  MapPin,
  Image as ImageIcon,
  Moon,
  Sun,
  Palette,
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_telenot-portal/artifacts/5ccwvbhz_PLU_Logo_Claim_RGB.png";

const AdminSettings = () => {
  const { user, logout, api } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const fileInputRef = useRef(null);

  // Reminder settings
  const [settings, setSettings] = useState({
    enabled: false,
    days_after_invitation: 7,
    max_reminders: 3,
    reminder_interval_days: 7,
  });

  // Company settings
  const [companyData, setCompanyData] = useState({
    company_name: "",
    address: "",
    email: "",
    phone: "",
    logo_url: LOGO_URL,
    custom_logo: null,
  });

  const isSuperAdmin = user?.role === "superadmin";

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    try {
      const [reminderRes, companyRes] = await Promise.all([
        api.get("/admin/reminder-settings"),
        api.get("/admin/company-settings"),
      ]);
      setSettings(reminderRes.data);
      setCompanyData(companyRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Einstellungen");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveReminders = async () => {
    setIsSaving(true);
    try {
      const params = new URLSearchParams({
        enabled: settings.enabled,
        days_after_invitation: settings.days_after_invitation,
        max_reminders: settings.max_reminders,
        reminder_interval_days: settings.reminder_interval_days,
      });
      await api.put(`/admin/reminder-settings?${params.toString()}`);
      toast.success("Erinnerungseinstellungen gespeichert");
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Speichern";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleProcessReminders = async () => {
    setIsProcessing(true);
    try {
      const response = await api.post("/admin/process-auto-reminders");
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Fehler beim Verarbeiten der Erinnerungen");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveCompany = async () => {
    setIsSavingCompany(true);
    try {
      const params = new URLSearchParams({
        company_name: companyData.company_name,
        address: companyData.address,
        email: companyData.email,
        phone: companyData.phone || "",
      });
      await api.put(`/admin/company-settings?${params.toString()}`);
      toast.success("Firmendaten gespeichert");
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Speichern";
      toast.error(message);
    } finally {
      setIsSavingCompany(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Nur Bilddateien sind erlaubt");
      return;
    }

    // Validate file size (1MB)
    if (file.size > 1 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 1MB)");
      return;
    }

    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/admin/company-logo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setCompanyData((prev) => ({
        ...prev,
        custom_logo: response.data.logo_url,
      }));
      toast.success("Logo hochgeladen");
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Hochladen";
      toast.error(message);
    } finally {
      setIsUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm("Logo wirklich löschen?")) return;

    try {
      await api.delete("/admin/company-logo");
      setCompanyData((prev) => ({
        ...prev,
        custom_logo: null,
      }));
      toast.success("Logo gelöscht");
    } catch (error) {
      toast.error("Fehler beim Löschen des Logos");
    }
  };

  const currentLogo = companyData.custom_logo || companyData.logo_url || LOGO_URL;

  return (
    <div className="page-bg min-h-screen">
      <AdminSidebar />
      <div className="ml-56">
        <AdminHeader />

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="font-manrope font-bold text-2xl text-foreground">
              Einstellungen
            </h1>
            <p className="text-muted-foreground mt-1">
              Konfigurieren Sie systemweite Einstellungen
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a5f] dark:border-blue-400"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Theme Settings Card */}
              <Card className="card-base" data-testid="theme-settings-card">
                <CardHeader className="border-b border-border bg-muted/50">
                  <CardTitle className="font-manrope text-lg flex items-center gap-2">
                    <Palette className="w-5 h-5 text-muted-foreground" />
                    Darstellung
                  </CardTitle>
                  <CardDescription>
                    Wählen Sie zwischen hellem und dunklem Design.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base flex items-center gap-2">
                      {theme === "dark" ? (
                        <Moon className="w-4 h-4" />
                      ) : (
                        <Sun className="w-4 h-4" />
                      )}
                      {theme === "dark" ? "Dunkles Design" : "Helles Design"}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {theme === "dark" 
                        ? "Klicken Sie auf den Schalter, um zum hellen Design zu wechseln" 
                        : "Klicken Sie auf den Schalter, um zum dunklen Design zu wechseln"}
                    </p>
                  </div>
                  <Switch
                    checked={theme === "dark"}
                    onCheckedChange={toggleTheme}
                    data-testid="theme-mode-toggle"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Company Settings Card */}
            <Card className="card-base" data-testid="company-settings-card">
              <CardHeader className="border-b border-border bg-muted/50">
                <CardTitle className="font-manrope text-lg flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-muted-foreground" />
                  Firmendaten
                </CardTitle>
                <CardDescription>
                  Diese Daten werden im PDF-Footer und in E-Mails angezeigt.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Logo Section */}
                <div className="space-y-4">
                  <Label className="flex items-center gap-2 text-base">
                    <ImageIcon className="w-4 h-4 text-slate-400" />
                    Firmenlogo
                  </Label>
                  <div className="flex items-center gap-6">
                    <div className="w-40 h-20 border border-slate-200 rounded-lg flex items-center justify-center bg-white p-2">
                      <img
                        src={currentLogo}
                        alt="Firmenlogo"
                        className="max-w-full max-h-full object-contain"
                        data-testid="company-logo-preview"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleLogoUpload}
                        accept="image/*"
                        className="hidden"
                        data-testid="logo-file-input"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploadingLogo}
                        data-testid="upload-logo-btn"
                      >
                        {isUploadingLogo ? (
                          <span className="flex items-center gap-2">
                            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900"></span>
                            Hochladen...
                          </span>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Logo hochladen
                          </>
                        )}
                      </Button>
                      {companyData.custom_logo && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleDeleteLogo}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid="delete-logo-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Löschen
                        </Button>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Max. 1MB, PNG oder JPG empfohlen
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-border pt-6 space-y-4">
                  {/* Company Name */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      Firmenname
                    </Label>
                    <Input
                      value={companyData.company_name}
                      onChange={(e) =>
                        setCompanyData({ ...companyData, company_name: e.target.value })
                      }
                      placeholder="plutec GmbH"
                      data-testid="company-name-input"
                    />
                  </div>

                  {/* Address */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      Adresse
                    </Label>
                    <Input
                      value={companyData.address}
                      onChange={(e) =>
                        setCompanyData({ ...companyData, address: e.target.value })
                      }
                      placeholder="04808 Lossatal"
                      data-testid="company-address-input"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      E-Mail
                    </Label>
                    <Input
                      type="email"
                      value={companyData.email}
                      onChange={(e) =>
                        setCompanyData({ ...companyData, email: e.target.value })
                      }
                      placeholder="info@firma.de"
                      data-testid="company-email-input"
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      Telefon
                    </Label>
                    <Input
                      value={companyData.phone}
                      onChange={(e) =>
                        setCompanyData({ ...companyData, phone: e.target.value })
                      }
                      placeholder="+49 123 456789"
                      data-testid="company-phone-input"
                    />
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-border">
                  <Button
                    onClick={handleSaveCompany}
                    disabled={isSavingCompany}
                    className="bg-[#1e3a5f] hover:bg-[#0d2137] text-white dark:bg-blue-600 dark:hover:bg-blue-700"
                    data-testid="save-company-btn"
                  >
                    {isSavingCompany ? "Speichern..." : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Firmendaten speichern
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Auto Reminder Settings Card - Now Individual per Customer */}
            <Card className="card-base" data-testid="reminder-settings-card">
              <CardHeader className="border-b border-border bg-muted/50">
                <CardTitle className="font-manrope text-lg flex items-center gap-2">
                  <Bell className="w-5 h-5 text-muted-foreground" />
                  Automatische Erinnerungen
                </CardTitle>
                <CardDescription>
                  Erinnerungseinstellungen werden jetzt individuell je Kunde konfiguriert.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Hinweis:</strong> Die Erinnerungseinstellungen werden jetzt für jeden Kunden 
                    individuell konfiguriert. Gehen Sie zur <strong>Benutzerverwaltung</strong>, 
                    wählen Sie einen Kunden und öffnen Sie den Tab "Erinnerungen".
                  </p>
                </div>
                
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Erinnerungen manuell für alle konfigurierten Kunden senden:
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleProcessReminders}
                    disabled={isProcessing}
                    data-testid="process-reminders-btn"
                  >
                    {isProcessing ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-foreground"></span>
                        Verarbeite...
                      </span>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Jetzt ausführen
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      </div>
    </div>
  );
};

export default AdminSettings;
